import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, FileText, Check, BookOpen } from "lucide-react";
import { DropZone } from "../../components/DropZone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const EUR = (v) => `${(Number(v) || 0).toFixed(2).replace(".", ",")} €`;
const EINHEITEN = ["g", "kg", "ml", "l", "Stück", "Portion"];

export const BelegeTab = ({ pwd, onImported }) => {
  const headers = { "X-Admin-Password": pwd };
  const [busy, setBusy] = useState("");
  const [beleg, setBeleg] = useState(null);
  const [positionen, setPositionen] = useState([]);
  const [rezept, setRezept] = useState(null);
  const [warteschlange, setWarteschlange] = useState([]);

  const upload = async (datei, pfad, setter) => {
    const fd = new FormData();
    fd.append("datei", datei);
    setBusy(pfad);
    try {
      const { data } = await axios.post(`${API}/admin/kalkulation/${pfad}`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setter(data);
      toast.success("Datei ausgelesen — bitte prüfen");
      return true;
    } catch (err) {
      toast.error(`${datei.name}: ${err.response?.data?.detail || "Auslesen fehlgeschlagen"}`);
      return false;
    } finally { setBusy(""); }
  };

  const belegLesen = (f) => upload(f, "beleg-lesen",
    (d) => { setBeleg(d.beleg); setPositionen(d.positionen || []); });

  const belegHoch = async (files) => {
    const [erste, ...rest] = files;
    setWarteschlange(rest);
    if (rest.length) toast.info(`${rest.length} weitere Datei(en) in der Warteschlange`);
    await belegLesen(erste);
  };

  const naechsterBeleg = async () => {
    setBeleg(null); setPositionen([]);
    const [naechste, ...rest] = warteschlange;
    if (!naechste) return;
    setWarteschlange(rest);
    await belegLesen(naechste);
  };

  const rezeptHoch = async (files) => {
    for (const f of files) {
      const ok = await upload(f, "rezept-lesen", setRezept);
      if (ok) break;
    }
  };

  const setPos = (i, patch) => setPositionen((ps) => ps.map((p, x) => (x === i ? { ...p, ...patch } : p)));

  const uebernehmen = async () => {
    const auswahl = positionen.filter((p) => p.uebernehmen && p.name && Number(p.einkauf_menge) > 0);
    if (!auswahl.length) { toast.error("Keine Position ausgewählt"); return; }
    setBusy("uebernehmen");
    try {
      const { data } = await axios.post(`${API}/admin/kalkulation/beleg-uebernehmen`, {
        beleg_id: beleg?.id || "", lieferant: beleg?.lieferant || "",
        positionen: auswahl.map((p) => ({
          name: p.name, artikelnummer: p.artikelnummer || "", einheit: p.einheit,
          einkauf_menge: Number(p.einkauf_menge), einkauf_preis: Number(p.einkauf_preis),
          typ: p.einheit === "Portion" ? "fertigprodukt" : "zutat",
          portionsgroesse: p.menge_gebinde || "", zutat_id: p.zutat_id || "",
        })),
      }, { headers });
      toast.success(`${data.neu} neu angelegt, ${data.aktualisiert} Preise aktualisiert`);
      setBusy("");
      await naechsterBeleg();
      onImported?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Übernahme fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const rezeptAnlegen = async () => {
    if (!rezept?.name) { toast.error("Kein Rezeptname erkannt"); return; }
    setBusy("rezept");
    try {
      await axios.post(`${API}/admin/kalkulation/rezepte`, {
        name: rezept.name, art: rezept.art, mengen_als_charge: rezept.mengen_als_charge,
        ergibt_portionen: rezept.ergibt_portionen, einheit_portion: rezept.einheit_portion,
        positionen: rezept.positionen.map((p) => (p.zutat_id
          ? { zutat_id: p.zutat_id, menge_pro_portion: Number(p.menge_pro_portion) }
          : { name: p.name, menge_pro_portion: Number(p.menge_pro_portion), einheit: p.einheit,
              einkauf_menge: 0, einkauf_preis: 0 })),
      }, { headers });
      toast.success("Rezept angelegt — unter Gänge & Zusätze prüfen");
      setRezept(null);
      onImported?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Rezept konnte nicht angelegt werden"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-8" data-testid="kalk-belege-tab">
      <p className="text-sm text-[#757067] max-w-2xl">
        Rechnung fotografieren oder PDF hochladen — die KI liest Artikel, Menge und Preis aus.
        Du prüfst die Liste und übernimmst sie: neue Artikel werden angelegt, bei bekannten wird nur
        der Preis aktualisiert (mit Preisverlauf). Der Beleg bleibt archiviert und an die Zutat verlinkt.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DropZone
          testid="kalk-beleg-drop"
          titel="Einkaufsrechnung"
          hinweis="Metro, Großmarkt, Verpackung … beliebige Dateien, auch mehrere"
          aktion="Rechnung hochladen"
          icon={Upload}
          multiple
          busy={busy === "beleg-lesen"}
          onFiles={belegHoch}
        />
        <DropZone
          testid="kalk-rezept-drop"
          titel="Rezept"
          hinweis="Foto, PDF oder Text — Zutaten werden mit Deiner Liste verknüpft"
          aktion="Rezept hochladen"
          icon={BookOpen}
          variant="outline"
          multiple
          busy={busy === "rezept-lesen"}
          onFiles={rezeptHoch}
        />
      </div>

      {warteschlange.length > 0 && (
        <div className="border border-[#E3DECE] bg-[#FAF5F1] p-4 flex items-center justify-between gap-4 flex-wrap"
          data-testid="kalk-beleg-warteschlange">
          <p className="text-xs text-[#575249]">
            Warteschlange: {warteschlange.length} Datei(en) — {warteschlange.map((f) => f.name).join(", ")}
          </p>
          <button onClick={naechsterBeleg} disabled={!!busy} data-testid="kalk-beleg-naechster"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-white px-4 py-2 text-[10px] uppercase tracking-[0.2em]">
            Nächsten Beleg lesen
          </button>
        </div>
      )}

      {beleg && (
        <div className="border border-[#E3DECE] bg-white p-5" data-testid="kalk-beleg-ergebnis">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="font-serif text-lg text-[#2C2A26]">{beleg.lieferant || "Beleg"}</p>
              <p className="text-xs text-[#757067] mt-1">
                {beleg.datum} {beleg.belegnummer ? `· Beleg ${beleg.belegnummer}` : ""} · {beleg.dateiname}
              </p>
            </div>
            <a href={`${API}/admin/kalkulation/beleg/${beleg.id}/datei?pwd=${encodeURIComponent(pwd)}`} target="_blank" rel="noreferrer"
              data-testid="kalk-beleg-ansehen"
              className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
              <FileText size={13} /> Beleg ansehen
            </a>
          </div>

          <div className="mt-5 space-y-2">
            {positionen.map((p, i) => (
              <div key={i} className="border border-[#F0EBE0] p-3" data-testid={`kalk-beleg-pos-${i}`}>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex items-center gap-2 pb-2.5">
                    <input type="checkbox" checked={!!p.uebernehmen} data-testid={`kalk-beleg-check-${i}`}
                      onChange={(e) => setPos(i, { uebernehmen: e.target.checked })} />
                  </label>
                  <div className="flex-1 min-w-[180px]">
                    <label className={LB}>Artikel</label>
                    <input value={p.name} data-testid={`kalk-beleg-name-${i}`}
                      onChange={(e) => setPos(i, { name: e.target.value })} className={IN} />
                  </div>
                  <div className="w-[110px]">
                    <label className={LB}>Menge</label>
                    <input type="number" step="0.01" value={p.einkauf_menge} data-testid={`kalk-beleg-menge-${i}`}
                      onChange={(e) => setPos(i, { einkauf_menge: e.target.value })} className={IN} />
                  </div>
                  <div className="w-[110px]">
                    <label className={LB}>Einheit</label>
                    <select value={p.einheit} data-testid={`kalk-beleg-einheit-${i}`}
                      onChange={(e) => setPos(i, { einheit: e.target.value })} className={IN}>
                      {EINHEITEN.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="w-[120px]">
                    <label className={LB}>Preis (€)</label>
                    <input type="number" step="0.01" value={p.einkauf_preis} data-testid={`kalk-beleg-preis-${i}`}
                      onChange={(e) => setPos(i, { einkauf_preis: e.target.value })} className={IN} />
                  </div>
                </div>
                <p className="text-xs text-[#757067] mt-2">
                  {p.zutat_id
                    ? <>bekannt als <b>{p.bekannt_als}</b> · bisher {p.alter_preis} → Preis wird aktualisiert</>
                    : <>neu in der Zutatenliste{p.einheit === "Portion" ? " · wird als Fertigprodukt angelegt" : ""}</>}
                  {Number(p.einkauf_menge) > 0 && (
                    <> · {EUR(Number(p.einkauf_preis) / Number(p.einkauf_menge))} pro {p.einheit}</>
                  )}
                  {p.menge_gebinde ? ` · ${p.menge_gebinde}` : ""}
                </p>
              </div>
            ))}
          </div>

          <button onClick={uebernehmen} disabled={busy === "uebernehmen"} data-testid="kalk-beleg-uebernehmen"
            className="mt-5 bg-[#2C2A26] text-white hover:bg-[#1F1D1A] px-5 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
            {busy === "uebernehmen" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            Ausgewählte übernehmen
          </button>
        </div>
      )}

      {rezept && (
        <div className="border border-[#E3DECE] bg-white p-5" data-testid="kalk-rezept-ergebnis">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className={LB}>Rezeptname</label>
              <input value={rezept.name} data-testid="kalk-rezept-gelesen-name"
                onChange={(e) => setRezept({ ...rezept, name: e.target.value })} className={IN} />
            </div>
            <div>
              <label className={LB}>Ansatz ergibt</label>
              <input type="number" step="1" value={rezept.ergibt_portionen} data-testid="kalk-rezept-gelesen-ergibt"
                onChange={(e) => setRezept({ ...rezept, ergibt_portionen: Number(e.target.value) })} className={IN} />
            </div>
            <div>
              <label className={LB}>Einheit</label>
              <input value={rezept.einheit_portion} data-testid="kalk-rezept-gelesen-einheit"
                onChange={(e) => setRezept({ ...rezept, einheit_portion: e.target.value })} className={IN} />
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            {rezept.positionen.map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-[#F0EBE0]">
                <span className="text-[#2C2A26]">{p.name}</span>
                <span className="text-[#757067]">
                  {p.menge_pro_portion} {p.einheit}
                  {p.zutat_id
                    ? <span className="text-[#BA6D52]"> · Preis aus „{p.bekannt_als}"</span>
                    : <span className="text-[#B03A2E]"> · Zutat fehlt noch</span>}
                </span>
              </div>
            ))}
          </div>
          {rezept.fehlende_zutaten?.length > 0 && (
            <p className="text-xs text-[#B03A2E] mt-3" data-testid="kalk-rezept-fehlende">
              Noch ohne Preis: {rezept.fehlende_zutaten.join(", ")} — lade die passende Rechnung hoch
              oder leg die Zutat an, dann rechnet das Rezept automatisch.
            </p>
          )}
          <button onClick={rezeptAnlegen} disabled={busy === "rezept"} data-testid="kalk-rezept-anlegen"
            className="mt-5 bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
            {busy === "rezept" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            Rezept anlegen
          </button>
        </div>
      )}
    </div>
  );
};
